import { useMemo, type ReactNode } from 'react';
import { RotateCw } from 'lucide-react';
import {
  Skeleton,
  Button,
  MultiSelect,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { WaapEmptyCard } from './WaapEmptyCard';
import type { WaapChart, WaapChartsResponse } from '@/api/waap';
import { useWaapWebsite } from '@/pages/services/waap/waap-context';
import { errMessage } from '@/pages/services/waap/waap-util';
import { WaapChartWidget } from './dashboard/WaapChartWidget';
import dashStyles from './DashboardTab.module.css';
import styles from './WaapAnalyticsBoard.module.css';

/** Bucket widgets by render lane: scalar KPIs, time-series (wide), pie + compact
 *  tables (split), and wide log tables (>5 cols → full width). */
export function groupCharts(charts: WaapChart[]) {
  const numbers: WaapChart[] = [];
  const wide: WaapChart[] = [];
  const split: WaapChart[] = [];
  const full: WaapChart[] = [];
  for (const c of charts) {
    if (c.kind === 'number') numbers.push(c);
    else if (c.kind === 'timeseries') wide.push(c);
    else if (c.kind === 'table' && (c.columns?.length ?? 0) > 5) full.push(c);
    else split.push(c);
  }
  return { numbers, wide, split, full };
}

/** Render a board's widgets into the standard four-lane grid layout. */
export function WaapChartGrid({ charts }: { charts: WaapChart[] }) {
  const g = useMemo(() => groupCharts(charts), [charts]);
  return (
    <div className={dashStyles.grids}>
      {g.numbers.length > 0 && (
        <div className={dashStyles.numberGrid}>
          {g.numbers.map((c) => (
            <WaapChartWidget key={c.key} chart={c} />
          ))}
        </div>
      )}
      {g.wide.length > 0 && (
        <div className={dashStyles.wideGrid}>
          {g.wide.map((c) => (
            <WaapChartWidget key={c.key} chart={c} />
          ))}
        </div>
      )}
      {g.split.length > 0 && (
        <div className={dashStyles.splitGrid}>
          {g.split.map((c) => (
            <WaapChartWidget key={c.key} chart={c} />
          ))}
        </div>
      )}
      {g.full.length > 0 && (
        <div className={dashStyles.fullGrid}>
          {g.full.map((c) => (
            <WaapChartWidget key={c.key} chart={c} />
          ))}
        </div>
      )}
    </div>
  );
}

/** Domain (hostname) multi-select sourced from the account's websites. */
function DomainFilter({ domains, onChange }: { domains: string[]; onChange: (d: string[]) => void }) {
  const { websites } = useWaapWebsite();
  const options = websites.map((w) => ({ value: w.domain, label: w.domain }));
  return (
    <div className={styles.filter}>
      <span className={styles.filterLabel}>Domain</span>
      <MultiSelect
        placeholder="All domains"
        options={options}
        value={domains}
        onChange={onChange}
        emptyHint="No websites available."
        className={styles.domainTrigger}
      />
    </div>
  );
}

export interface WaapBoardQuery {
  data: WaapChartsResponse | null | undefined;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  /** Refetch the board's data (drives the toolbar Refresh button). react-query
   *  supplies both; optional so callers passing a bare shape still type-check. */
  refetch?: () => void;
  isFetching?: boolean;
}

export interface WaapAnalyticsBoardProps<R extends string> {
  /** Icon + label drive the empty/not-configured states ("{label} analytics …"). */
  icon: ReactNode;
  label: string;
  ranges: readonly R[];
  range: R;
  onRangeChange: (r: R) => void;
  /** Omit `domains`/`onDomainsChange` for boards without a Domain filter (API). */
  domains?: string[];
  onDomainsChange?: (d: string[]) => void;
  query: WaapBoardQuery;
}

/**
 * Shared shell for every native-charts Analytics board: a Domain + Time Range
 * toolbar, the website/loading/not-configured/error states, and the four-lane
 * chart grid. Each tab supplies only its data hook + range/domain state.
 */
export function WaapAnalyticsBoard<R extends string>({
  icon,
  label,
  ranges,
  range,
  onRangeChange,
  domains,
  onDomainsChange,
  query,
}: WaapAnalyticsBoardProps<R>) {
  const { selected, isLoading: websitesLoading } = useWaapWebsite();
  const domainId = selected?.domainId;
  const { data, isLoading, isError, error, refetch, isFetching } = query;

  if (websitesLoading) return <Skeleton height={520} />;
  if (!domainId) {
    return (
      <WaapEmptyCard
        icon={icon}
        title="No websites yet"
        description={`Add a website under this account to view its ${label} analytics.`}
      />
    );
  }

  return (
    <div className={dashStyles.grids}>
      <div className={styles.toolbar}>
        {domains && onDomainsChange && <DomainFilter domains={domains} onChange={onDomainsChange} />}
        <div className={styles.filter}>
          <span className={styles.filterLabel}>Time Range</span>
          <Select value={range} onValueChange={(v) => onRangeChange(v as R)}>
            <SelectTrigger aria-label="Time Range" className={styles.rangeSelect}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ranges.map((r) => (
                <SelectItem key={r} value={r}>
                  {r}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {refetch && (
          <Button
            variant="secondary"
            onClick={() => refetch()}
            disabled={isFetching}
            className={styles.refreshBtn}
          >
            <RotateCw size={14} />
            Refresh
          </Button>
        )}
      </div>

      {isLoading ? (
        <Skeleton height={480} />
      ) : data === null ? (
        <WaapEmptyCard
          icon={icon}
          title={`${label[0].toUpperCase()}${label.slice(1)} analytics not configured`}
          description="This board is not set up for this environment yet. Ask an administrator to enable it."
        />
      ) : isError ? (
        <Alert tone="danger" title={`Couldn't load ${label}`}>
          {errMessage(error)}
        </Alert>
      ) : (
        <WaapChartGrid charts={data?.charts ?? []} />
      )}
    </div>
  );
}
